"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@devhop/ui";
import { ChevronLeftIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { WorkshopJobDetail } from "../api/client";
import { WorkshopJobPartsPanel } from "./WorkshopJobPartsPanel";
import {
  WorkshopPartSourceChoice,
  type WorkshopPartAddSourceMode,
} from "./WorkshopPartSourceChoice";

export function WorkshopPartsDialog({
  open,
  onOpenChange,
  shopId,
  job,
  canEdit,
  confirmLabel,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  job: WorkshopJobDetail;
  canEdit: boolean;
  confirmLabel?: string;
  onConfirm?: () => void | Promise<void>;
  pending?: boolean;
}) {
  const partsCount = job.parts?.length ?? 0;
  const [sourceMode, setSourceMode] = useState<WorkshopPartAddSourceMode | null>(
    null,
  );

  useEffect(() => {
    if (open) setSourceMode(null);
  }, [open]);

  const choosingSource = sourceMode === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92vh,760px)] w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 space-y-1 border-border border-b px-4 py-4 sm:px-6">
          <DialogTitle className="text-base sm:text-lg">
            {choosingSource
              ? "ອາໄຫຼ່ມາຈາກໃສ?"
              : confirmLabel
                ? "ເລືອກອາໄຫຼ່"
                : "ແກ້ໄຂອາໄຫຼ່"}
          </DialogTitle>
          <DialogDescription className="text-left text-xs sm:text-sm">
            {choosingSource
              ? "ເລືອກປະເພດອາໄຫຼ່ກ່ອນ ແລ້ວຄ່ອຍເພີ່ມລາຍການ"
              : `ໃບງານ ${job.jobNumber} · ${partsCount} ລາຍການ`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          {choosingSource ? (
            <WorkshopPartSourceChoice onSelect={setSourceMode} />
          ) : (
            <>
              <button
                type="button"
                className="mb-3 inline-flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
                onClick={() => setSourceMode(null)}
              >
                <ChevronLeftIcon className="size-3.5" />
                ປ່ຽນປະເພດອາໄຫຼ່
              </button>
              <WorkshopJobPartsPanel
                shopId={shopId}
                job={job}
                canEdit={canEdit}
                defaultAddOpen
                addSourceMode={sourceMode}
                dialogMode
              />
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-border border-t px-4 py-3 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="h-11 flex-1 sm:flex-none"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            {onConfirm ? "ຍົກເລີກ" : "ປິດ"}
          </Button>
          {onConfirm && confirmLabel && !choosingSource ? (
            <Button
              type="button"
              className="h-11 flex-1 sm:flex-none"
              disabled={pending}
              onClick={() => void onConfirm()}
            >
              {pending ? "ກຳລັງບັນທຶກ…" : confirmLabel}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
